import { get, isNil } from 'lodash';
import moment from 'moment';
import { all, call, put, select, take } from 'redux-saga/effects';
import { selectOperatorGroupId, selectRole, selectSite } from '../../store/authReducer';
import { refreshRequest, REFRESH_SUCCESS } from '../../views/login/actions';
import { selectCoreSiteId, selectSiteGroupId } from '../../views/settings/reducer';
import { hasRole, RoleTypes } from '../components/Permission';
import { TOKEN_HEADER } from '../constants';
import QueueAction from '../queueAction';
import { getJwtToken, getRefreshToken, getSelectedSite } from '../storage';
import * as toastMessages from '../toastMessages';
import { getErrorStatus, isSilentError } from './errorPolicy';

export function* notifyNetworkError(error, meta) {
  // The errorApi branch toasts a canned message and never sees the error
  // object, so the permission check has to happen here rather than in apiError.
  if (isSilentError(error)) {
    return;
  }

  if (meta && meta?.errorApi) {
    yield call(toastMessages.handledApiError, meta.errorMessage);
  } else {
    yield call(toastMessages.apiError, error);
  }
}

function* handleUnauthorized({ fn, data, options }, tokens, meta) {
  yield put(refreshRequest(tokens));

  yield take(REFRESH_SUCCESS);

  const newOptions = {
    ...options,
    headers: {
      [TOKEN_HEADER]: yield call(getJwtToken),
    },
  };

  try {
    return yield call(fn, { data, options: newOptions });
  } catch (error) {
    return yield notifyNetworkError(error, meta);
  }
}

// wrapper for api calls which handles the errors and other common concerns
export default function* apiRequest(fn, data, args) {
  const jwtToken = yield call(getJwtToken);
  const refreshToken = yield call(getRefreshToken);
  const meta = getMetaData(args);
  const siteId = meta?.useCoreSiteIdParam
    ? yield select(selectCoreSiteId)
    : yield select(selectSite);

  let groupId = yield select(selectOperatorGroupId);

  if (!groupId) {
    const role = yield select(selectRole);

    if (hasRole(role, RoleTypes.Admin)) {
      groupId = yield select(selectSiteGroupId);
    }
  }

  const options = {
    headers: {
      [TOKEN_HEADER]: jwtToken,
    },
    siteId,
    groupId,
  };

  const apiRequestProperties = { fn, data, options };

  try {
    return yield call(fn, { data, options });
  } catch (error) {
    // Same normalization as the notify gate below: a backend 401 arrives as
    // `responseStatus`, so matching only `statusCode` skipped the token refresh.
    if (getErrorStatus(error) === 401) {
      return yield handleUnauthorized(apiRequestProperties, { jwtToken, refreshToken }, meta);
    }

    // api.js rejects with `{ responseStatus, ...body }`, so gating on `statusCode`
    // alone swallowed every backend error whose body did not repeat the status.
    if (getErrorStatus(error) !== null) {
      yield notifyNetworkError(error, meta);
    }

    throw error;
  }
}

function getMetaData(args = {}) {
  const queueAction = get(args, 'meta.queueAction');

  let meta = args.meta;

  if (!isNil(queueAction)) {
    meta = {
      ...meta,
      queueAction: QueueAction.RemoveFromQueue,
    };
  }

  return meta;
}

export function getRange(startDate, endDate, type) {
  let fromDate = moment(startDate);
  let toDate = moment(endDate);
  return toDate.diff(fromDate, type);
}

// don't have to call this if need for special handling,
// just call apiRequest directly and handle errors normally in saga
export function* apiSaga(fn, data, action, args) {
  try {
    yield put({ type: `${action}_REQUEST` });

    const response = yield call(apiRequest, fn, data, args);

    const meta = getMetaData(args);

    yield put({ type: `${action}_SUCCESS`, response, meta });

    if (args && args.closeModal) {
      yield put({ type: 'HIDE_DIALOG' });
    }

    if (args && args.navigate) {
      yield call(args.navigate, response);
    }

    if (args && args.callback) {
      if (!Array.isArray(args.callback)) {
        const nextAction = args.callback();
        if (nextAction) {
          yield put(nextAction);
        }
      } else {
        const actions = args.callback
          .map((cb) => cb())
          .filter(Boolean)
          // eslint-disable-next-line
          .map(function (nextAction) {
            return put(nextAction);
          });
        if (actions.length) {
          yield all(actions);
        }
      }
    }

    if (args && args.voidCallback) {
      yield call(args.voidCallback);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line
      console.error(error);
    }

    const meta = getMetaData(args);

    if (meta && meta?.errorApi) {
      if (meta?.errorApi === 'fetchSite') {
        meta.navigate();
      }
    }

    if (args && args.errorCallback) {
      if (!Array.isArray(args.errorCallback)) {
        const nextAction = args.errorCallback(error);
        if (nextAction) {
          yield put(nextAction);
        }
      } else {
        const actions = args.errorCallback
          .map((cb) => cb(error))
          .filter(Boolean)
          .map(function (nextAction) {
            return put(nextAction);
          });
        if (actions.length) {
          yield all(actions);
        }
      }
    }

    yield put({ type: `${action}_FAILURE`, error, meta });
  }
}
